import { LOGOUT_USER } from "~/store/action_types";
import { optimisticUpdateResolver } from "~/utils/optimisticUpdateResolver";

export function callAPIMiddleware({ dispatch, getState }) {
  /**
   * Cache will be valid for 5 minutes
   * if value is not set otherwise
   */
  const defaultValidFor = 300000;

  /**
   * Request Cache store object
   */
  const requestCacheDictionary = {};

  const setResolveTime = (cacheKey) => {
    if (cacheKey) {
      const resolveDate = new Date();
      requestCacheDictionary[cacheKey].resolvedAt = resolveDate.getTime();
    }
  };

  return (next) => (action) => {
    const { types, callAPI, shouldCallAPI = () => true, payload = {}, cache = {} } = action;

    if (!types) {
      // Normal action: pass it on
      return next(action);
    }

    if (!Array.isArray(types) || types.length !== 3 || !types.every((type) => typeof type === `string`)) {
      throw new Error(`Expected an array of three string types.`);
    }

    if (typeof callAPI !== `function`) {
      throw new Error(`Expected fetch to be a function.`);
    }

    if (!shouldCallAPI(getState())) {
      return;
    }

    const [requestType, successType, failureType] = types;

    const loadingAction = Object.assign({}, payload, {
      type: requestType,
    });

    const _previous = optimisticUpdateResolver(loadingAction, getState());

    dispatch(loadingAction);

    const date = new Date();
    const { key: cacheKey, validFor = defaultValidFor } = cache;

    if (typeof cacheKey === "string" && typeof requestCacheDictionary[cacheKey] === "undefined") {
      process.env.NODE_ENV === "development" &&
        // eslint-disable-next-line no-console
        console.info(`%cRequest Cache Register: %c${cacheKey}`, "color: DodgerBlue", "color: inherit");
      requestCacheDictionary[cacheKey] = {
        requestedAt: date.getTime(),
        resolvedAt: 0,
      };
    }

    const requestPromise = callAPI().then(
      (response) => {
        setResolveTime(cacheKey);
        return dispatch(
          Object.assign(
            {},
            payload,
            {
              // eslint-disable-next-line no-prototype-builtins
              response: response.hasOwnProperty(`_parsedBody`) ? response._parsedBody : response,
              type: successType,
              _previous,
            },
            // eslint-disable-next-line no-prototype-builtins
            response.hasOwnProperty(`_headers`) ? { responseHeaders: response._headers } : {}
          )
        );
      },
      (error) => {
        const { response = {}, status_code, code } = error;
        if (error.name === "RedirectException") {
          error.message = "";
          requestCacheDictionary[cacheKey] = undefined;
        }

        if (status_code === 401) {
          dispatch({ type: LOGOUT_USER });
        }

        if (error.name === "AbortError") {
          throw error;
        }

        if (process.env.NODE_ENV === "test" && code === "ERR_NOCK_NO_MATCH") {
          throw error;
        }

        return dispatch(
          Object.assign({}, payload, {
            error,
            response,
            type: failureType,
            _previous,
          })
        );
      }
    );

    const { resolvedAt } = requestCacheDictionary[cacheKey] ?? {};

    if (process.env.NODE_ENV === "development") {
      if (typeof resolvedAt !== "number") {
        // eslint-disable-next-line no-console
        console.info(`%cRequest Cache Skip: %c${successType}`, "color: red", "color: inherit");
      } else if (resolvedAt > 0) {
        const isHit = resolvedAt + validFor > date.getTime();
        const color = isHit ? "color: green" : "color: orange";
        const text = isHit ? `%cRequest Cache Hit: %c${cacheKey}` : `%cRequest Cache Miss: %c${cacheKey}`;
        // eslint-disable-next-line no-console
        console.info(text, color, "color: inherit");
      }
    }

    return typeof resolvedAt === "number" && resolvedAt + validFor > date.getTime()
      ? Promise.resolve({})
      : requestPromise;
  };
}
